import { inject, Injectable } from '@angular/core';
import { Action, Store } from '@ngrx/store';
import { Actions, createEffect, ofType } from '@ngrx/effects';

import * as actions from './email-actors.actions';
import { EmailActorsCreateOrUpdateAction, EmailActorsDeleteAction } from './email-actors.actions';
import { Observable, of } from 'rxjs';
import { RootState } from '..';
import { EmailActorsRestService } from '../../services/rest/email-actors.rest.service';
import { AbstractEffects } from '../AbstractEffects';
import { TranslateService } from '@ngx-translate/core';
import { AzureMonitoringService } from '../../services/AzureMonitoringService';
import { catchError, filter, map, switchMap, withLatestFrom } from 'rxjs/operators';
import { NotificationService } from 'app/core/services/notification.service';
import { AuthenticationService } from '../../authentication/authentication.service';

@Injectable({ providedIn: 'root' })
export class EmailActorsEffects extends AbstractEffects {
  private authenticationService = inject(AuthenticationService);
  createEmailActors$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType<EmailActorsCreateOrUpdateAction>(actions.EMAIL_ACTORS_CREATE_OR_UPDATE),
      withLatestFrom(this.store, (action: EmailActorsCreateOrUpdateAction, state) => {
        return { payload: action.payload, state };
      }),
      switchMap(
        (
          { payload, state } // Register to this event, unregister previous if new event occurs.
        ) =>
          this.emailActorsRestService
            .createEmailActors(payload.emailActor, state.session.selectedCustomer.loadSuccess && state.session.selectedCustomer.data?.id) // Create email actor
            .pipe(
              map(response => new actions.EmailActorsCreateOrUpdateSuccessAction(response)),
              catchError(err => this.handleFail(err, 'ERRORS.FILTER_FAILED.TITLE', [of(new actions.EmailActorsCreateOrUpdateFailAction(err))]))
            )
      )
    )
  );

  deleteEmailActors$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType<EmailActorsDeleteAction>(actions.EMAIL_ACTORS_DELETE),
      withLatestFrom(this.store, (action: EmailActorsDeleteAction, state) => {
        return { payload: action.payload, state };
      }),
      switchMap(
        (
          { payload, state } // Register to this event, unregister previous if new event occurs.
        ) =>
          this.emailActorsRestService
            .deleteEmailActor(payload.id) // Delete email actor
            .pipe(
              map(response => new actions.EmailActorsDeleteSuccessAction(response)),
              catchError(err => this.handleFail(err, 'ERRORS.FILTER_FAILED.TITLE', [of(new actions.EmailActorsDeleteFailAction(err))]))
            )
      )
    )
  );

  constructor(
    private store: Store<RootState>,
    azureMonitoringService: AzureMonitoringService,
    private actions$: Actions,
    private emailActorsRestService: EmailActorsRestService,
    translateService: TranslateService,
    notificationService: NotificationService
  ) {
    super(translateService, azureMonitoringService, notificationService);
  }

  loadEmailActors$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.EMAIL_ACTORS_LOAD),
      withLatestFrom(this.authenticationService.authenticated$, this.store, (action, isAuthenticated, state) => ({
        isAuthenticated,
        state,
      })),
      filter(({ isAuthenticated }: { isAuthenticated: boolean }) => isAuthenticated),
      switchMap(
        (
          { state }: { state: RootState; isAuthenticated: boolean } // Register to this event, unregister previous if new event occurs.
        ) =>
          this.emailActorsRestService
            .getEmailActors(state.session.selectedCustomer.loadSuccess && state.session.selectedCustomer.data?.id) // Request email actors
            .pipe(
              map(itemArray => new actions.EmailActorsLoadSuccessAction(itemArray)),
              catchError(err => this.handleFail(err, 'ERRORS.FILTER_FAILED.TITLE', [of(new actions.EmailActorsLoadFailAction(err))]))
            )
      )
    )
  )
}
